import { useQuery } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"
import { type ReactNode, Suspense, useMemo } from "react"
import { Link, NavLink, useLocation, useParams } from "react-router-dom"
import { BotStatusBadge, botBuilderKeys, getBot } from "@/entities/bot-builder"
import { useSessionRole } from "@/entities/session"
import { useScenarioDraft } from "@/features/edit-scenario-draft"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageTransition } from "@/shared/ui/page-transition"
import { Skeleton } from "@/shared/ui/skeleton"
import {
	BotWorkspaceContext,
	botPath,
	InnerPageContext,
	type ScenarioDraftState,
} from "../model/bot-workspace-context"
import {
	findNavItem,
	isInnerSection,
	WORKSPACE_NAV,
	type WorkspaceNavSection,
} from "../model/workspace-nav"
import { PublishButton } from "./publish-button"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import { RouteSwap } from "@/shared/ui/route-swap"
import { SlidingPill } from "@/shared/ui/sliding-pill"
import { PageSkeleton } from "@/shared/ui/page-skeleton"

/** Редактор воронки занимает весь экран и сам рисует шапку. */
const FULL_BLEED_PATH = /^constructor\/funnels\/[^/]+$/

const isIn = (sectionPath: string, to: string) =>
	sectionPath === to || sectionPath.startsWith(`${to}/`)

/** Ссылка назад к списку ботов, как у внутренних страниц: стрелка и название списка. */
function BackLink() {
	return (
		<Link
			to="/admin/bots"
			className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
		>
			<ArrowLeft className="size-4" />
			Боты
		</Link>
	)
}

function SaveError({ draft }: { draft: ScenarioDraftState }) {
	if (!draft.saveError) return null
	return (
		<div
			role="alert"
			className="flex flex-wrap items-center gap-3 rounded-2xl border border-destructive/60 bg-card p-4 text-sm"
		>
			<span className="min-w-0 flex-1">{draft.saveError}</span>
			<Button size="sm" onClick={() => void draft.reload()}>
				Загрузить актуальную версию
			</Button>
		</div>
	)
}

/**
 * Разделы бота вкладками, подразделы строкой ссылок под ними. Так раздел бота живёт внутри
 * общей панели, с её меню слева, а не отдельным приложением поверх неё.
 */
function WorkspaceTabs({
	botId,
	sections,
	sectionPath,
}: {
	botId: string
	sections: WorkspaceNavSection[]
	sectionPath: string
}) {
	// Страницы вне меню (одна рассылка, один пользователь) относятся к разделу по первому сегменту.
	const root = sectionPath.split("/")[0]
	const active =
		sections.find((section) => section.items.some((item) => isIn(sectionPath, item.to))) ??
		sections.find((section) => section.items.some((item) => item.to.split("/")[0] === root))
	return (
		<nav aria-label="Разделы бота" className="mb-6">
			<div className="hide-scrollbar -mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
				<div className="relative flex w-max gap-1 rounded-full bg-secondary/60 p-1">
					<SlidingPill className="rounded-full" />
					{sections.map((section) => {
						const current = section === active
						return (
							<Link
								key={section.title}
								to={botPath(botId, section.items[0]!.to)}
								aria-current={current ? "page" : undefined}
								data-active={current}
								className={cn(
									"relative flex items-center gap-2 rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors",
									current ? "text-foreground" : "text-muted-foreground hover:text-foreground",
								)}
							>
								<section.icon className="relative size-4" />
								<span className="relative">{section.title}</span>
							</Link>
						)
					})}
				</div>
			</div>
			{/* Подразделы меняются вместе с разделом перекрёстным переходом, а выбранный
			    подраздел подсвечивает подложка, которая переезжает между пунктами. */}
			<LoadSwap loading={false} id={active?.title ?? "none"}>
				{active && active.items.length > 1 ? (
					<div className="hide-scrollbar relative -mx-3 flex gap-1 pt-3 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0">
						<SlidingPill className="rounded-full bg-secondary shadow-none ring-1 ring-border" />
						{active.items.map((item) => {
							const current = isIn(sectionPath, item.to)
							return (
								<NavLink
									key={item.to}
									to={botPath(botId, item.to)}
									data-active={current}
									className={cn(
										"relative rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
										current
											? "text-foreground"
											: "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
									)}
								>
									{item.label}
								</NavLink>
							)
						})}
					</div>
				) : null}
			</LoadSwap>
		</nav>
	)
}

/** Заглушка шапки бота: надзаголовок, имя, @username со статусом и кнопка публикации справа. */
function HeaderSkeleton() {
	return (
		<div aria-hidden="true" className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
			<div className="min-w-0">
				<div className="flex h-[16.5px] items-center">
					<Skeleton className="h-3 w-9 rounded" />
				</div>
				<div className="mt-1 flex h-8 items-center sm:h-9">
					<Skeleton className="h-6 w-56 rounded-lg sm:h-7" />
				</div>
				<div className="mt-2 flex h-[22px] items-center gap-2">
					<Skeleton className="h-4 w-28 rounded" />
					<Skeleton className="h-5 w-20 rounded-full" />
				</div>
			</div>
			<Skeleton className="h-9 w-36 rounded-xl" />
		</div>
	)
}

/** Заглушка раздела, пока грузится бот: заголовок с пояснением и блок содержимого. */
function SectionSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-5">
			<div className="space-y-2">
				<Skeleton className="h-6 w-40 rounded-lg" />
				<Skeleton className="h-4 w-72 max-w-full rounded" />
			</div>
			<Skeleton className="h-64 w-full rounded-2xl" />
		</div>
	)
}

/** Заглушка внутренней страницы: ссылка назад, надзаголовок, название и блок содержимого. */
function InnerSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-6">
			<div>
				<div className="mb-4 flex h-5 items-center">
					<Skeleton className="h-3.5 w-24 rounded" />
				</div>
				<div className="flex h-[16.5px] items-center">
					<Skeleton className="h-3 w-28 rounded" />
				</div>
				<div className="mt-1 flex h-8 items-center sm:h-9">
					<Skeleton className="h-6 w-64 rounded-lg sm:h-7" />
				</div>
			</div>
			<Skeleton className="h-96 w-full rounded-2xl" />
		</div>
	)
}

/**
 * Раздел бота: шапка страницы как у остальной панели, вкладки разделов и страница раздела.
 * Вкладки от данных бота не зависят и стоят сразу, пока шапка и раздел грузятся заглушками.
 * Страницы разделов приходят в `children` маршрутами с застывшим адресом, чтобы уходящий
 * раздел до конца показывал себя.
 */
export function BotWorkspaceLayout({ children }: { children: ReactNode }) {
	const { botId = "" } = useParams()
	const location = useLocation()
	const { isModerator, isAdmin } = useSessionRole()
	const bot = useQuery({
		queryKey: botBuilderKeys.bot(botId),
		queryFn: () => getBot(botId),
		enabled: isModerator && !!botId,
	})
	const scenarioId = bot.data?.scenarioId ?? ""
	const draft = useScenarioDraft(scenarioId, isModerator && !!scenarioId)
	const workspace = useMemo(
		() => (bot.data ? { bot: bot.data, draft: bot.data.scenarioId ? draft : null } : null),
		[bot.data, draft],
	)
	const sections = useMemo(
		() =>
			WORKSPACE_NAV.map((section) => ({
				...section,
				items: section.items.filter((item) => isAdmin || !item.adminOnly),
			})).filter((section) => section.items.length > 0),
		[isAdmin],
	)

	if (!isModerator) return <AccessDenied title="Бот" />

	const prefix = botPath(botId)
	const sectionPath = location.pathname.startsWith(`${prefix}/`)
		? location.pathname.slice(prefix.length + 1)
		: ""

	if (FULL_BLEED_PATH.test(sectionPath)) {
		return (
			<div className="fixed inset-0 z-40 flex flex-col bg-background">
				<LoadSwap loading={bot.isLoading} fill>
					{workspace ? (
						<BotWorkspaceContext.Provider value={workspace}>
							<div className="flex h-full flex-col">
								<Reveal show={!!draft.saveError} className="px-3 pt-3">
									<SaveError draft={draft} />
								</Reveal>
								<div className="min-h-0 flex-1">
									<Suspense fallback={null}>{children}</Suspense>
								</div>
							</div>
						</BotWorkspaceContext.Provider>
					) : bot.isLoading ? (
						<PageSkeleton className="p-6" />
					) : (
						<div className="p-6">
							<LoadErrorState
								message="Не удалось загрузить бота."
								onRetry={() => void bot.refetch()}
							/>
						</div>
					)}
				</LoadSwap>
			</div>
		)
	}

	const navItem = findNavItem(sectionPath)

	const glow = (
		<div
			aria-hidden
			className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_16%,transparent),transparent_70%)]"
		/>
	)

	if (navItem && isInnerSection(sectionPath)) {
		const inner = {
			back: { to: botPath(botId, navItem.to), label: navItem.label },
			botName: bot.data?.name ?? "",
		}
		return (
			<PageTransition>
				<div className="relative isolate">
					{glow}
					<div className="container mx-auto max-w-6xl px-3 py-5 sm:px-6 lg:py-8">
						<LoadSwap loading={bot.isLoading} id={workspace ? "workspace" : "error"}>
							{workspace ? (
								<BotWorkspaceContext.Provider value={workspace}>
									<InnerPageContext.Provider value={inner}>
										<Reveal show={!!draft.saveError} className="pb-4">
											<SaveError draft={draft} />
										</Reveal>
										<Suspense fallback={<InnerSkeleton />}>{children}</Suspense>
									</InnerPageContext.Provider>
								</BotWorkspaceContext.Provider>
							) : bot.isLoading ? (
								<InnerSkeleton />
							) : (
								<LoadErrorState
									message="Не удалось загрузить бота."
									onRetry={() => void bot.refetch()}
								/>
							)}
						</LoadSwap>
					</div>
				</div>
			</PageTransition>
		)
	}

	return (
		<PageTransition>
			<div className="relative isolate">
				{glow}
				<div className="container mx-auto max-w-6xl px-3 py-5 sm:px-6 lg:py-8">
					<BackLink />

					<LoadSwap loading={bot.isLoading} id={bot.data ? "bot" : "error"} className="mb-6">
						{bot.data ? (
							<header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
								<div className="min-w-0">
									<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
										Бот
									</p>
									<h1 className="mt-1 truncate text-2xl font-bold tracking-tight sm:text-3xl">
										{bot.data.name}
									</h1>
									<p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
										<span className="truncate">@{bot.data.username}</span>
										<BotStatusBadge status={bot.data.status} />
									</p>
								</div>
								{bot.data.scenarioId ? (
									<div className="flex items-center gap-3">
										<PublishButton bot={bot.data} draft={draft} />
									</div>
								) : null}
							</header>
						) : (
							<HeaderSkeleton />
						)}
					</LoadSwap>

					<WorkspaceTabs botId={botId} sections={sections} sectionPath={sectionPath} />

					<LoadSwap loading={bot.isLoading} id={workspace ? "workspace" : "error"}>
						{workspace ? (
							<BotWorkspaceContext.Provider value={workspace}>
								<Reveal show={!!draft.saveError} className="pb-4">
										<SaveError draft={draft} />
									</Reveal>
									<Suspense fallback={<PageSkeleton />}>
										<RouteSwap id={location.pathname}>{children}</RouteSwap>
									</Suspense>
							</BotWorkspaceContext.Provider>
						) : bot.isLoading ? (
							<SectionSkeleton />
						) : (
							<LoadErrorState
								message="Не удалось загрузить бота."
								onRetry={() => void bot.refetch()}
							/>
						)}
					</LoadSwap>
				</div>
			</div>
		</PageTransition>
	)
}
